/* eslint-disable @typescript-eslint/no-empty-function */
import React, { useState, useEffect, FC } from 'react';
import {
  Grid,
  Button,
  Box,
  useMediaQuery,
  useTheme,
  FormGroup,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import ReCAPTCHA from 'react-google-recaptcha';
import { RiArrowRightSLine } from 'react-icons/ri';

import {
  NewWidgetDateTimeField,
  NewVehicalCounter,
  WidgetNotificationBox,
  NewWidgetLocationField,
} from 'views/rebranded-module/common/components/UI/NewMainWidget';
import { LOCATION_TYPE } from 'lib/utils/mainWidgetConstants';
import StyledGrid from 'views/rebranded-module/common/components/UI/NewMainWidget/NewRentalWidget/NewRentalWidget.styles';
import { useContent } from 'views/rebranded-module/common/context/useContent';
import { TourOnlyNotice } from 'views/common/components/UI/MainWidget/TourOnlyNotice';
import getConfig from 'next/config';

const { publicRuntimeConfig } = getConfig();
const { envVars } = publicRuntimeConfig;

const NewRentalWidget: FC = (props: any) => {
  const {
    vertical,
    pickupData,
    dropoffData,
    motorcycleList,
    handleLocationInputChange,
    formData,
    handleFormDataChange,
    shouldDisableDate,
    returnToSameLocation,
    handleReturnToSameLocation,
    isOptionsLoading,
    handleSearchButtonClick,
    renderAllWidget = true,
    handleRenderAllWidget = () => {},
    bordered,
    rentalWidgetNotifications,
    disableVehicleCount,
    actionButtonCustomLabel,
    errors,
    disabled,
    hasToReturnToSameLocation,
    isDealWidget,
    hiddenVehicleCount,
    hiddenSearchBtn,
    openDropoffDatePicker,
    resetOpenDropoffDatePicker,
    recaptchaRef,
    recaptchaVehicleCountThreshold,
    whiteLabelWidget = false,
    isTourOnly,
    tourOnlySlug,
  } = props;

  const cmsData = useContent('NewMainWidgetComponent');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [widgetMounted, setWidgetMounted] = useState(false);

  const handleSearch = (recaptchaToken: any) => {
    if (renderAllWidget) {
      handleSearchButtonClick(recaptchaToken);
    } else {
      handleRenderAllWidget(true);
    }
  };

  const handleRecaptchaButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    if (
      !hiddenSearchBtn
      && formData.reservation.vehicles_count >= recaptchaVehicleCountThreshold
      && renderAllWidget
    ) {
      recaptchaRef.current.execute();
    } else {
      handleSearch(null);
    }
  };

  useEffect(() => {
    setWidgetMounted(true);
  }, []);

  useEffect(() => {
    if (widgetMounted) {
      if (isMobile) {
        if (formData.dataFromQuery) {
          handleReturnToSameLocation(false, true);
          if (
            formData.reservation?.pickup_location_id
            == formData.reservation?.dropoff_location_id
          ) {
            handleReturnToSameLocation(true);
          }
          handleRenderAllWidget(true);
        } else {
          handleReturnToSameLocation(true);
        }
      } else {
        handleReturnToSameLocation(false, true);
        handleRenderAllWidget(true);
      }
    }
  }, [widgetMounted, isMobile]);

  return (
    <StyledGrid container rowSpacing={1.5} vertical={vertical}>
      {isTourOnly && (
        <Grid item className="RWNoticeGrid">
          <TourOnlyNotice slug={tourOnlySlug} />
        </Grid>
      )}
      {(rentalWidgetNotifications?.[0] || errors?.reservation_widget_base) && (
        <Grid item xs={12}>
          {errors?.reservation_widget_base && (
            <Box>
              <WidgetNotificationBox
                title={cmsData.baseErrorHeader}
                notificationsList={errors?.reservation_widget_base}
              />
            </Box>
          )}
          {rentalWidgetNotifications?.[0] && (
            <Box>
              <WidgetNotificationBox notificationsList={rentalWidgetNotifications} />
            </Box>
          )}
        </Grid>
      )}

      <Grid container item spacing={vertical ? 1.5 : { md: 0, xs: 1.5 }} className="RWLTwoInputInRow">
        <Grid item xs={12} md={vertical ? 12 : 6} position="relative" className="RWInputContainer">
          <NewWidgetLocationField
            pickup={LOCATION_TYPE.PICKUP}
            selectedLocationId={formData.reservation?.pickup_location_id}
            locationsList={pickupData.locations}
            vertical={vertical}
            bordered={bordered}
            label={cmsData.pickupLocation}
            placeholder={cmsData.choosePickupLocation}
            handleInputChange={handleLocationInputChange}
            handleLocationChange={handleFormDataChange}
            handleRenderAllWidget={handleRenderAllWidget}
            loading={isOptionsLoading[LOCATION_TYPE.PICKUP]}
            errors={errors}
            hasArrow="true"
            disabled={disabled}
          />
          {!vertical && (
            <Box className="RWArrowIcon">
              <RiArrowRightSLine />
            </Box>
          )}
        </Grid>

        {renderAllWidget && (
          <>
            {!whiteLabelWidget && (
              <Grid item xs={12} className="RWCheckBox">
                <Box>
                  <FormGroup>
                    <FormControlLabel
                      checked={returnToSameLocation}
                      onChange={(e) => handleReturnToSameLocation(
                        (e.target as HTMLInputElement).checked,
                      )}
                      control={<Checkbox defaultChecked />}
                      label={cmsData.returnToSameLocation}
                      disabled={disabled}
                    />
                  </FormGroup>
                </Box>
              </Grid>
            )}

            {(!returnToSameLocation || whiteLabelWidget) && (
              <Grid item xs={12} md={vertical ? 12 : 6}>
                <NewWidgetLocationField
                  pickup={LOCATION_TYPE.DROPOFF}
                  selectedLocationId={formData.reservation?.dropoff_location_id}
                  locationsList={dropoffData.locations}
                  vertical={vertical}
                  hasArrow="false"
                  label={cmsData.dropoffLocation}
                  placeholder={
                    hasToReturnToSameLocation
                      ? cmsData.sameAsPickupLocation
                      : cmsData.chooseDropoffLocation

                  }
                  handleInputChange={handleLocationInputChange}
                  handleLocationChange={handleFormDataChange}
                  loading={isOptionsLoading[LOCATION_TYPE.DROPOFF]}
                  errors={errors}
                  disabled={disabled}
                  isDealWidget={isDealWidget}
                />
              </Grid>
            )}
          </>
        )}
      </Grid>

      {/* DateTime Fields */}
      <Grid container item spacing={vertical ? 1.5 : { md: 0, xs: 1.5 }} className="RWLTwoInputInRow">
        {renderAllWidget && (
          <>
            <Grid item xs={12} md={vertical ? 12 : 6} position="relative" className="RWInputContainer">
              <NewWidgetDateTimeField
                pickup={LOCATION_TYPE.PICKUP}
                vertical={vertical}
                bordered={bordered}
                timeList={pickupData.timeSlots}
                dataLabel={cmsData.pickupDateLabel}
                timeLabel={cmsData.timeLabel}
                date={formData.reservation?.pickup_date}
                time={formData.reservation?.pickup_time_slot}
                minDate={pickupData.minDate}
                maxDate={pickupData.maxDate}
                handleDateTimeChange={handleFormDataChange}
                shouldDisableDate={shouldDisableDate}
                errors={errors}
                disabled={disabled}
                whiteLabelWidget={whiteLabelWidget}
              />
              {!vertical && (
                <Box className="RWArrowIcon">
                  <RiArrowRightSLine />
                </Box>
              )}
            </Grid>

            <Grid item xs={12} md={vertical ? 12 : 6}>
              <NewWidgetDateTimeField
                pickup={LOCATION_TYPE.DROPOFF}
                vertical={vertical}
                bordered={bordered}
                timeList={dropoffData.timeSlots}
                dataLabel={cmsData.dropoffDateLabel}
                timeLabel={cmsData.timeLabel}
                date={formData.reservation?.dropoff_date}
                time={formData.reservation?.dropoff_time_slot}
                minDate={dropoffData.minDate}
                maxDate={dropoffData.maxDate}
                handleDateTimeChange={handleFormDataChange}
                shouldDisableDate={shouldDisableDate}
                errors={errors}
                disabled={disabled}
                openWidgetDateTimeField={openDropoffDatePicker}
                resetOpenWidgetDateTimeField={resetOpenDropoffDatePicker}
                whiteLabelWidget={whiteLabelWidget}
              />
            </Grid>
          </>
        )}
      </Grid>

      {/* Vehicle Count + Search Button */}
      <Grid
        container
        item
        rowSpacing={whiteLabelWidget ? 1.5 : 2}
      >
        {!hiddenVehicleCount && (
          <Grid item xs={12} md={vertical ? 12 : 8}>
            <NewVehicalCounter
              errors={errors}
              disableVehicleCount={disableVehicleCount || disabled}
              handleFormDataChange={handleFormDataChange}
              formData={formData}
              motorcycleList={motorcycleList}
            />
          </Grid>
        )}

        {!hiddenSearchBtn && (
          <Grid item xs={12} md={vertical ? 12 : 4} className="RWSearchNowButtonGrid">
            <Button
              variant="contained"
              className="RWSearchNowButton"
              id="rentalWidgetSearchNowBtn"
              data-testid="reserve-search-now-btn"
              fullWidth
              size="large"
              disableElevation
              disabled={isTourOnly || disabled}
              onClick={handleRecaptchaButtonClick}
            >
              {actionButtonCustomLabel || cmsData.reserveBtnLabel}
            </Button>
          </Grid>
        )}

        {envVars.INVISIBLE_RECAPTCHA_SITE_KEY
          && !hiddenSearchBtn
          && formData.reservation.vehicles_count
            >= recaptchaVehicleCountThreshold
          && renderAllWidget && (
            <ReCAPTCHA
              className="SACRecaptchaBadge"
              ref={recaptchaRef}
              sitekey={envVars.INVISIBLE_RECAPTCHA_SITE_KEY}
              size="invisible"
              onChange={(recaptchaToken) => {
                handleSearch(recaptchaToken);
              }}
            />
        )}
      </Grid>
    </StyledGrid>
  );
};

export { NewRentalWidget };